import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, Search, MessageCircle, Loader2, Inbox, Trash2 } from "lucide-react";
import { StatusBadge, STATUS_LIST, STATUS_META } from "@/components/dashboard/status";

export default function LeadsTable({ isAdmin }) {
  const [leads, setLeads] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fSeller, setFSeller] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Debounce del buscador para que responda con fluidez
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (fSeller !== "all") params.seller_id = fSeller;
    if (fStatus !== "all") params.status = fStatus;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error("Error al cargar leads");
    } finally {
      setLoading(false);
    }
  }, [search, fSeller, fStatus, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/sellers").then(({ data }) => setSellers(data)).catch(() => {});
  }, [isAdmin]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  const reassign = async (id, seller_id) => {
    try {
      const { data } = await api.patch(`/leads/${id}/reassign`, { seller_id });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success("Lead reasignado");
    } catch (e) {
      toast.error("No se pudo reasignar");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/leads/${deleteTarget.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success("Registro eliminado");
      setDeleteTarget(null);
    } catch (e) {
      toast.error("No se pudo eliminar el registro");
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (fSeller !== "all") params.seller_id = fSeller;
      if (fStatus !== "all") params.status = fStatus;
      const res = await api.get("/leads/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  const clearFilters = () => {
    setSearchInput(""); setSearch(""); setFSeller("all"); setFStatus("all"); setDateFrom(""); setDateTo("");
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const activeFilters = search || fSeller !== "all" || fStatus !== "all" || dateFrom || dateTo;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-50">CRM de Leads</h1>
          <p className="text-slate-400 text-sm">{leads.length} leads mostrados</p>
        </div>
        {isAdmin && (
          <Button data-testid="export-csv-button" onClick={exportCsv} className="bg-[#25D366] hover:bg-[#22BF5B] text-[#0B0F14] font-semibold">
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
        )}
      </div>

      <div className="bg-[#111822] border border-[#1E2D3D] rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input data-testid="lead-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Cliente o teléfono" className="pl-9 bg-[#0B0F14] border-[#1E2D3D] text-slate-100" />
        </div>
        {isAdmin && (
          <Select value={fSeller} onValueChange={setFSeller}>
            <SelectTrigger data-testid="filter-seller-select" className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100"><SelectValue placeholder="Vendedor" /></SelectTrigger>
            <SelectContent className="bg-[#111822] border-[#1E2D3D] text-slate-100">
              <SelectItem value="all">Todos los vendedores</SelectItem>
              {sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filter-status-select" className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent className="bg-[#111822] border-[#1E2D3D] text-slate-100">
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2 items-center">
          <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100 text-xs" />
          <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-[#0B0F14] border-[#1E2D3D] text-slate-100 text-xs" />
        </div>
        {activeFilters && (
          <button data-testid="clear-filters-button" onClick={clearFilters} className="md:col-span-4 justify-self-start text-xs text-slate-400 hover:text-[#25D366] transition-colors">
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="bg-[#111822] border border-[#1E2D3D] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#25D366] animate-spin" /></div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 text-slate-500"><Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />No hay leads que coincidan</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1E2D3D] text-left text-xs font-mono-lf uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Teléfono</th>
                  <th className="px-4 py-3">Fuente</th>
                  {isAdmin && <th className="px-4 py-3">Vendedor</th>}
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Acción</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#1E2D3D]/50 hover:bg-[#172230] transition-colors">
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap font-mono-lf text-xs">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-slate-200">{l.name || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 text-slate-300 font-mono-lf text-xs">{l.phone || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{l.source}</td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <Select value={l.seller_id || undefined} onValueChange={(v) => reassign(l.id, v)}>
                          <SelectTrigger data-testid={`reassign-select-${l.id}`} className="h-8 w-36 bg-[#0B0F14] border-[#1E2D3D] text-slate-200 text-xs">
                            <SelectValue placeholder={l.is_backup ? "Respaldo" : "Sin asignar"} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111822] border-[#1E2D3D] text-slate-100">
                            {sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger data-testid={`status-select-${l.id}`} className="h-8 w-auto border-0 bg-transparent p-0 focus:ring-0">
                          <StatusBadge status={l.status} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111822] border-[#1E2D3D] text-slate-100">
                          {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {l.seller_phone && (
                          <a data-testid={`wa-link-${l.id}`} href={`https://wa.me/${l.seller_phone}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[#25D366] hover:text-[#4ADE80] text-xs font-medium">
                            <MessageCircle className="w-4 h-4" /> Chat
                          </a>
                        )}
                        {isAdmin && (
                          <button
                            data-testid={`delete-lead-button-${l.id}`}
                            onClick={() => setDeleteTarget(l)}
                            title="Eliminar registro"
                            className="inline-flex items-center text-slate-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-lead-dialog" translate="no" className="bg-[#111822] border-[#1E2D3D] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-slate-100">¿Seguro que deseas eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              <span>{`Se eliminará únicamente el lead${deleteTarget?.name ? ` de "${deleteTarget.name}"` : deleteTarget?.phone ? ` con teléfono ${deleteTarget.phone}` : ""}. Esta acción no se puede deshacer y no afecta a los demás registros.`}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="bg-transparent border-[#1E2D3D] text-slate-300 hover:bg-[#172230] hover:text-slate-100">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-red-500 hover:bg-red-600 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
